import {
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  Badge,
  Button,
  Checkbox,
  ColumnManager,
  EmptyState,
  Panel,
  PanelHeader,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { formatMoneyText } from '@/components/ui/Money';
import { Tooltip } from '@/components/ui/Tooltip';
import { CompactFigure } from '@/components/dashboards/TargetTiles';
import { cx } from '@/lib/cx';
import { saveBlob } from '@/lib/download';
import { Notice } from '@/components/ui/Notice';
import {
  heatLevel,
  matrixToCsv,
  projectMatrix,
  sortRows,
  type MatrixRow,
  type MatrixSort,
  type RevenueMatrix,
  type VisibleMatrix,
  type VisibleMatrixRow,
} from '@/lib/revenue-matrix';
import {
  HIDE_ADD_ONS_ID,
  HIDE_ADD_ONS_LABEL,
  cycleColumnKey,
  hiddenByAddOnSwitch,
  isKeyShown,
  pickerController,
} from '@/lib/matrix-visibility';
import type { MatrixVisibility } from '@/lib/use-matrix-visibility';
import { compactMoneyParts } from '@/lib/targets';

/**
 * A REVENUE MATRIX: rows × cycles — the grid read of what was bought and when.
 * Used for one company's products (`CompanyRevenueMatrix`, rows = products)
 * and for one product's companies (rows = companies). The table knows nothing
 * about what a row is: `rowHeader` names the column, `renderRowLabel` may
 * replace the label text (e.g. with a `<Link>` to the record).
 *
 * Every figure comes pre-computed from `lib/revenue-matrix.ts` as an exact
 * decimal string in ONE display currency (`currencyCode`). Nothing here adds,
 * converts or parses money: the cells are compacted for the eye through
 * `compactMoneyParts` (the one compact formatter, the same one
 * `RevenueCompanyTable` uses) and the exact figure travels on each cell's
 * Tooltip and accessible name.
 *
 * ── LAYOUT ──────────────────────────────────────────────────────────────────
 *
 * <rowHeader> | cycle … (oldest → current) | Total | Share. The bottom row is
 * the column totals with the grand total, and under each cycle's total its
 * change on the previous column (`matrix.yoy`, already signed and rounded by
 * the lib). The label column is frozen left; on a phone the same table scrolls
 * inside `TableWrap` (owner decision: no card list for this grid). A row's
 * optional `sublabel` sits muted after its label.
 *
 * ── INTERACTION ─────────────────────────────────────────────────────────────
 *
 * - Hover (or keyboard focus) lights a crosshair: a light fill on the cell's
 *   row and column. When the pointer leaves the grid, the crosshair falls back
 *   to the keyboard-focused cell rather than clearing.
 * - Clicking a cell PINS its row and column, drawn as inset EDGE LINES (never a
 *   fill, so a pin cannot be mistaken for heat), with a ring on the clicked
 *   cell. The same cell again, or Esc pressed inside the grid, clears it.
 *   Clicking a row label pins just that row. Only the row-label buttons carry
 *   `aria-pressed`; amount buttons are named by their figure alone.
 * - Every header sorts. Only the sorted header carries `aria-sort` (the rest
 *   omit it). Share sorts by total (the same order; a null share last), but
 *   only the header that was clicked carries the indicator, so the screen
 *   never claims two sorts at once.
 * - The body is a roving-tabindex grid: one tab stop, arrows move, Home/End go
 *   to the row's ends, Ctrl+Home/End to the grid's corners.
 *
 * ── ROW LABEL WITH A LINK (`renderRowLabel`) ────────────────────────────────
 *
 * A link cannot live inside the pin `<button>` (nested interactive content).
 * So when `renderRowLabel` is given, the label cell uses the SAME pattern as
 * the amount cells: the pin toggle is a full-cell overlay `<button>` (the
 * roving grid stop, `aria-label` "Pin <label> row", `aria-pressed`), and the
 * label content sits above it with `pointer-events-none`, except the rendered
 * label node itself, which takes pointer events. Clicking the name text
 * follows the link; clicking anywhere else in the cell pins the row. The link
 * keeps its own Tab stop (a link must be reachable without the grid model),
 * and arrow keys pressed while it has focus move through the grid as if from
 * that row's label cell (`data-row-label`). Without `renderRowLabel` the label
 * is the pin button itself, as before.
 *
 * ⚠️ **HIGHLIGHT AND HEAT ARE OVERLAY SPANS, NOT CELL BACKGROUNDS, on the frozen
 * column.** A frozen `TH`/`TD` bakes in an opaque `bg-surface`, and a second
 * `bg-*` utility through `className` races it on stylesheet order. The amount
 * cells are not frozen, so their heat tint can sit on the cell itself.
 *
 * ── HIDING YEARS AND ROWS (owner, 2026-10-27) ────────────────────────────
 *
 * Two pickers beside Export CSV — `Columns` (the years) and `Products` /
 * `Companies` (the rows) — both the shared `ColumnManager`, fed by
 * `pickerController` over one panel-level `useMatrixVisibility` per dimension
 * (see `matrix-visibility.ts` for what is stored and why). The products picker
 * also carries the "Hide add-ons" switch, decided by the catalogue flag.
 *
 * ⚠️ **EVERYTHING BELOW THE PICKERS READS `view`, NEVER `matrix`.** `view` is
 * `projectMatrix(matrix, …)`: only the visible rows × visible years, with the
 * row totals, column totals, grand total, shares, YoY and the heat scale
 * recomputed over exactly those — so the sort by Total, the shading and the
 * CSV all follow what is on screen. A figure read off `matrix` here would
 * describe rows or years the reader has hidden.
 *
 * ⚠️ **A KEPT ROW WHOSE VISIBLE CELLS ARE ALL EMPTY STAYS, WITH DASHES** — only
 * the reader hides a row. When nothing at all is left (every year hidden here,
 * or every row hidden by a choice made in another matrix or by the switch),
 * the grid is replaced by an empty state whose action shows everything again.
 *
 * ⚠️ **A YEAR'S SORT IS REMEMBERED BY ITS ID (`cycle:<id>`), NOT ITS POSITION**,
 * because hiding a year moves every position after it. A remembered position
 * from before 2026-10-27 falls back to the default order.
 *
 * ⚠️ **HEAT USES THE `info` TOKEN, NOT `accent-brand`.** The accent is reserved
 * for primary actions and active state; a sequential tint across a whole grid
 * is neither. Opacity steps keep it readable on both themes because the token
 * itself flips with the theme.
 */

/* -------------------------------------------------------------------------
 * Static class tables — written out whole so Tailwind's source scan sees them.
 * --------------------------------------------------------------------- */

const HEAT_CLASS: Record<0 | 1 | 2 | 3 | 4 | 5, string> = {
  0: '',
  1: 'bg-info/[0.06]',
  2: 'bg-info/[0.11]',
  3: 'bg-info/[0.17]',
  4: 'bg-info/[0.24]',
  5: 'bg-info/[0.28]',
};

/** The hover crosshair: a light FILL. Always the same colour, faded in and out. */
const HOVER_CLASS = {
  none: 'bg-fg/[0.04] opacity-0',
  hover: 'bg-fg/[0.04] opacity-100',
} as const;

/**
 * Which pinned line(s) a cell sits on. A pin is drawn as inset EDGE LINES,
 * never a fill, so a pinned row or column can never be read as heat.
 */
type PinAxis = 'none' | 'row' | 'col' | 'both';

const PIN_CLASS: Record<PinAxis, string> = {
  none: '',
  row: 'shadow-[inset_0_1px_0_0_var(--fg-subtle),inset_0_-1px_0_0_var(--fg-subtle)]',
  col: 'shadow-[inset_1px_0_0_0_var(--fg-subtle),inset_-1px_0_0_0_var(--fg-subtle)]',
  both: 'shadow-[inset_0_1px_0_0_var(--fg-subtle),inset_0_-1px_0_0_var(--fg-subtle),inset_1px_0_0_0_var(--fg-subtle),inset_-1px_0_0_0_var(--fg-subtle)]',
};

/**
 * The anchor of a pinned crosshair — the cell that was clicked. Its own span
 * over the pin lines: two `shadow-*` utilities on one element do not merge.
 */
const ANCHOR_RING = 'shadow-[inset_0_0_0_1px_var(--fg-muted)]';

/**
 * The totals row's frozen label cell: its right edge AND the row's top rule in
 * one declaration. Inline, because `TH` bakes in a `shadow-*` for the edge
 * alone, and a second shadow utility would race it; a style always wins. The
 * row's own `border-t` is a collapsed border, painted under this sticky cell.
 */
const TOTALS_LABEL_SHADOW = {
  boxShadow: 'inset -1px 0 0 0 var(--hairline), inset 0 1px 0 0 var(--hairline-strong)',
} as const;

/* -------------------------------------------------------------------------
 * The long-table scroll box (owner decision 2026-10-16)
 *
 * Every row always shows (no limit). Above SCROLL_THRESHOLD rows the table
 * scrolls inside its own box, capped at 60vh and never under 320px, with the
 * header row stuck to the top, the totals row stuck to the bottom and the
 * label column stuck to the left. At or below the threshold the markup and
 * classes are exactly the plain `TableWrap` table.
 *
 * ⚠️ **Our own scroller, not `TableWrap`.** `TableWrap` is `overflow-x-auto`
 * only, which is a scroll container of its own: a `top-0` inside it would
 * stick to a box that never scrolls vertically. Its `frozenHeader` mode
 * sizes itself to the viewport bottom, not 60vh, and its header context is
 * private. So this box is `relative overflow-auto`; `relative` is load-bearing
 * for the same reason `TableWrap` records (absolute `sr-only` spans must not
 * escape the clip and stretch the page).
 *
 * ⚠️ **`sticky` REPLACES `relative` on the cells, never joins it.** Both are
 * `position`, so two utilities race on stylesheet order. A sticky cell is a
 * containing block already, so the overlays (Wash, cell buttons) still fit.
 *
 * ⚠️ **The row rules are spans, not borders or shadows.** `border-collapse`
 * paints a row's border at the grid line, so it stays behind while the sticky
 * cells move; and the frozen label cells already spend their one `box-shadow`
 * on their right edge. A 1px absolute span carries the rule with the cell.
 *
 * z-index, inside the page-content band of the ladder (0–20): frozen label
 * header 20 (from `TH`), other header cells 15, frozen body/footer label
 * cells 10 (from `TH`), other footer cells 5, body cells unlayered. Each
 * sticky cell paints over exactly the cells that scroll under it.
 * --------------------------------------------------------------------- */

const SCROLL_THRESHOLD = 14;

const SCROLL_BOX =
  'relative w-full overflow-auto max-h-[max(320px,60vh)] scroll-pt-9 scroll-pb-14 scroll-pl-40 sm:scroll-pl-52';

const STICKY_HEAD_CELL = 'sticky top-0 z-[15] bg-surface-sunken';

const STICKY_FOOT_CELL = 'sticky bottom-0 z-[5] bg-surface';

/** The header row's bottom rule, carried by each sticky header cell. */
function HeadRule(): ReactElement {
  return (
    <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-border" />
  );
}

/** The Total column's left rule, on its sticky header and footer cells (their `border-l` stays behind). */
function LeftRule(): ReactElement {
  return <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-px bg-border" />;
}

/** The totals row's top rule, carried by each sticky footer cell. */
function FootRule(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 h-px bg-hairline-strong"
    />
  );
}

/* -------------------------------------------------------------------------
 * Sort model
 * --------------------------------------------------------------------- */

/**
 * A header's identity: the label column, a year column BY ITS KEY
 * (`cycle:<id>`), Total or Share. ⚠️ *SUPERSEDED 2026-10-27: a year column by
 * index* — an index points at a different year as soon as one is hidden.
 */
type ColumnId = 'label' | 'total' | 'share' | `cycle:${string}`;

type SortState = { column: ColumnId; dir: 'asc' | 'desc' };

/** The lib's own default order, so the first paint matches the matrix as built. */
const DEFAULT_SORT: SortState = { column: 'total', dir: 'desc' };

/** A remembered `SortState` (filter memory), or `undefined` (an old numeric index too). */
function parseSortState(raw: unknown): SortState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const { column, dir } = raw as { column?: unknown; dir?: unknown };
  if (dir !== 'asc' && dir !== 'desc') return undefined;
  if (column === 'label' || column === 'total' || column === 'share') return { column, dir };
  if (typeof column === 'string' && column.startsWith('cycle:') && column.length > 6) {
    return { column: column as `cycle:${string}`, dir };
  }
  return undefined;
}

function toMatrixSort(sort: SortState, view: VisibleMatrix): MatrixSort {
  if (sort.column === 'label') return { by: 'label', dir: sort.dir };
  // Share is total ÷ grand total, so it orders exactly as total does.
  if (sort.column === 'total' || sort.column === 'share') return { by: 'total', dir: sort.dir };
  const cycleIndex = view.cycles.findIndex((cycle) => cycleColumnKey(cycle.id) === sort.column);
  return { by: { cycleIndex }, dir: sort.dir };
}

/* -------------------------------------------------------------------------
 * Small presentational pieces
 * --------------------------------------------------------------------- */

function SortChevron({ state }: { state: 'asc' | 'desc' | null }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx(
        'h-3.5 w-3.5 shrink-0 transition duration-150 ease-out motion-reduce:transition-none',
        state === null
          ? 'opacity-0 group-hover/sort:opacity-50 group-focus-visible/sort:opacity-50'
          : 'text-fg opacity-100',
        state === 'asc' && 'rotate-180',
      )}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function DownloadIcon(): ReactElement {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5" />
      <path d="M3.5 13.5v1A2.5 2.5 0 0 0 6 17h8a2.5 2.5 0 0 0 2.5-2.5v-1" />
    </svg>
  );
}

/** A compacted figure (`£61.25K`). The exact value is the caller's Tooltip. */
function Figure({
  amount,
  currencyCode,
  strong,
}: {
  amount: string;
  currencyCode: string;
  strong?: boolean;
}): ReactElement {
  return (
    <span className={cx('whitespace-nowrap text-fg tabular', strong ? 'font-semibold' : 'font-medium')}>
      <CompactFigure parts={compactMoneyParts(amount, currencyCode)} />
    </span>
  );
}

/**
 * A non-interactive total: compacted for the eye, exact on hover and for a
 * screen reader (the compacted text is hidden from it, the exact one is not).
 */
function ExactFigure({ amount, currencyCode }: { amount: string; currencyCode: string }): ReactElement {
  const exact = formatMoneyText(amount, currencyCode);
  return (
    <Tooltip label={exact} describes={false}>
      <span>
        <span aria-hidden="true">
          <Figure amount={amount} currencyCode={currencyCode} strong />
        </span>
        <span className="sr-only">{exact}</span>
      </span>
    </Tooltip>
  );
}

/** The faint dash for "no line in this cycle" — never a zero. */
function NoFigure(): ReactElement {
  return <span className="text-fg-subtle">—</span>;
}

/**
 * The highlight layers. First child of a positioned cell, under its content:
 * the hover fill, the pin's edge lines along `axis`, and the anchor ring —
 * three spans, because each pin/anchor literal is a whole `box-shadow`.
 */
function Wash({
  hover,
  axis,
  anchor = false,
}: {
  hover: boolean;
  axis: PinAxis;
  anchor?: boolean;
}): ReactElement {
  return (
    <>
      <span
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute inset-0 transition-opacity duration-100 ease-out motion-reduce:transition-none',
          HOVER_CLASS[hover ? 'hover' : 'none'],
        )}
      />
      {axis !== 'none' && (
        <span aria-hidden="true" className={cx('pointer-events-none absolute inset-0', PIN_CLASS[axis])} />
      )}
      {anchor && (
        <span aria-hidden="true" className={cx('pointer-events-none absolute inset-0', ANCHOR_RING)} />
      )}
    </>
  );
}

/** `"12.3"` → `"12.3%"`; the lib already rounded it. */
function percentText(value: string): string {
  return `${value}%`;
}

/** The year-on-year line under a column total. Signed by the lib. */
function YoyChange({ value }: { value: string | null }): ReactElement {
  if (value === null) {
    return (
      <span className="text-2xs text-fg-subtle">
        <span className="sr-only">Change on previous cycle: </span>—
      </span>
    );
  }
  const tone = value.startsWith('-')
    ? 'text-danger'
    : value.startsWith('+')
      ? 'text-success'
      : 'text-fg-subtle';
  return (
    <span className={cx('text-2xs font-medium tabular', tone)}>
      <span className="sr-only">Change on previous cycle: </span>
      {percentText(value)}
    </span>
  );
}

/** The muted qualifier after a row label; nothing when the row has none. */
function Sublabel({ text }: { text: string | null | undefined }): ReactElement | null {
  if (text === null || text === undefined || text.trim() === '') return null;
  return <span className="shrink-0 text-xs text-fg-subtle">{text}</span>;
}

/**
 * The row's share of the grand total, or null when the lib gives none (the
 * grand total is zero or below). Widened here so the code holds whether the
 * lib's `share` type is `string` or `string | null`.
 */
function shareOf(row: VisibleMatrixRow): string | null {
  return row.share;
}

/** True when the grand total is absent, zero or below: no share column means anything then. */
function isNonPositive(value: string | null): boolean {
  if (value === null) return true;
  return value.trim().startsWith('-') || /^0+(\.0+)?$/.test(value.trim());
}

/** The row's name for accessible labels: the label, with its sublabel when it has one. */
function rowName(row: Pick<MatrixRow, 'label' | 'sublabel'>): string {
  const sub = row.sublabel?.trim();
  return sub ? `${row.label} (${sub})` : row.label;
}

/** "Product" → "products", "Company" → "companies": the panel description's noun. */
function rowNoun(rowHeader: string, count: number): string {
  const one = rowHeader.trim().toLowerCase();
  if (count === 1) return one;
  if (/[^aeiou]y$/.test(one)) return `${one.slice(0, -1)}ies`;
  return `${one}s`;
}

/* -------------------------------------------------------------------------
 * The component
 * --------------------------------------------------------------------- */

export interface MatrixVisibilityProps {
  /** The years, stored as `cycle:<id>`. */
  cycles: MatrixVisibility;
  /** The rows, stored as the row key. */
  rows: MatrixVisibility;
  /** The rows picker's word: `Products` or `Companies`. */
  rowsTitle: string;
  /**
   * The products dimension only: the row keys of add-on products, from the
   * catalogue flag. Given ⇒ the rows picker carries the "Hide add-ons" switch;
   * `keys: null` ⇒ not known yet (or the read failed) and the switch is disabled.
   */
  addOns?: { keys: ReadonlySet<string> | null; failed: boolean };
}

export interface RevenueMatrixTableProps {
  matrix: RevenueMatrix;
  /** The ONE unit every figure in `matrix` is already in. */
  currencyCode: string;
  /** The panel title, e.g. "Revenue by product". */
  title: string;
  /** Whose revenue this is (the company or the product); handed to the CSV builder. */
  subjectName: string;
  /** The label column's header, singular ("Product", "Company"); also the CSV's first header. */
  rowHeader: string;
  /** The downloaded file's full name, including `.csv`. */
  csvFileName: string;
  /**
   * Replaces the label TEXT of a row (e.g. a `<Link>` to the record). The
   * sublabel and the "Not in catalogue" badge still render after it, and the row stays
   * pinnable through the rest of the cell (see the header comment).
   */
  renderRowLabel?: (row: VisibleMatrixRow) => ReactNode;
  /**
   * What the reader has hidden — one `useMatrixVisibility` per dimension,
   * mounted by the PANEL so every expanded matrix in it shares the choice.
   */
  visibility: MatrixVisibilityProps;
  /**
   * What the figures are, the description's first clause. Each caller states its
   * own basis: Revenue by Company passes `Paid, incl. tax` (owner, 2026-10-16).
   * The default is the Revenue by Product tab's historical basis, so a caller
   * that says nothing keeps the caption it had.
   */
  basisLabel?: string;
}

type CrossHair = { rowKey: string | null; col: number | null };

const NO_CROSSHAIR: CrossHair = { rowKey: null, col: null };

/** Reads the grid position of an event target: a `data-cell`, or inside a row's custom label. */
function positionOf(target: EventTarget | null): { r: number; c: number } | null {
  if (!(target instanceof HTMLElement)) return null;
  const raw = target.dataset.cell;
  if (raw !== undefined) {
    const [rText, cText] = raw.split(':');
    return { r: Number(rText), c: Number(cText) };
  }
  const labelHost = target.closest<HTMLElement>('[data-row-label]');
  if (labelHost?.dataset.rowLabel !== undefined) {
    return { r: Number(labelHost.dataset.rowLabel), c: -1 };
  }
  return null;
}

export function RevenueMatrixTable({
  matrix,
  currencyCode,
  title,
  subjectName,
  rowHeader,
  csvFileName,
  renderRowLabel,
  visibility,
  basisLabel = 'Paid, ex-tax',
}: RevenueMatrixTableProps): ReactElement {
  const { cycles: cycleVis, rows: rowVis, rowsTitle, addOns } = visibility;
  const addOnKeys = addOns?.keys ?? null;

  /*
   * What the reader sees — see "HIDING YEARS AND ROWS" in the header. Every
   * figure below reads `view`.
   */
  const view = useMemo(
    () =>
      projectMatrix(
        matrix,
        (cycle) => isKeyShown(cycleColumnKey(cycle.id), cycleVis.hidden),
        (row) => isKeyShown(row.key, rowVis.hidden, addOnKeys),
      ),
    [matrix, cycleVis.hidden, rowVis.hidden, addOnKeys],
  );

  const cycleKeys = useMemo(() => matrix.cycles.map((cycle) => cycleColumnKey(cycle.id)), [matrix.cycles]);
  const rowKeys = useMemo(() => matrix.rows.map((row) => row.key), [matrix.rows]);
  const cycleNames = useMemo(
    () => new Map<string, string>(matrix.cycles.map((cycle) => [cycleColumnKey(cycle.id), cycle.name])),
    [matrix.cycles],
  );
  const rowNames = useMemo(() => new Map(matrix.rows.map((row) => [row.key, rowName(row)])), [matrix.rows]);

  const cyclePicker = pickerController({
    liveKeys: cycleKeys,
    hidden: cycleVis.hidden,
    loading: cycleVis.loading,
    setHidden: cycleVis.setHidden,
  });
  const rowPicker = pickerController({
    liveKeys: rowKeys,
    hidden: rowVis.hidden,
    loading: rowVis.loading,
    addOnRowKeys: addOnKeys,
    setHidden: rowVis.setHidden,
  });
  // A row held back by the switch is listed, ticked off and disabled, with the reason.
  const switchHeldKeys = rowKeys.filter((key) => hiddenByAddOnSwitch(key, rowVis.hidden, addOnKeys));
  const hideAddOnsOn = rowVis.hidden.has(HIDE_ADD_ONS_ID);
  const rowItemNoun = rowHeader.trim().toLowerCase();

  const addOnSwitch =
    addOns === undefined ? undefined : (
      <Checkbox
        label={HIDE_ADD_ONS_LABEL}
        checked={hideAddOnsOn}
        disabled={addOns.keys === null || rowVis.loading}
        {...(addOns.keys === null
          ? {
              title: addOns.failed
                ? 'The product list could not be read, so add-ons cannot be told apart'
                : 'Reading which products are add-ons',
            }
          : {})}
        onChange={() => rowVis.setHidden([HIDE_ADD_ONS_ID], !hideAddOnsOn)}
      />
    );

  const showEverything = () => {
    cycleVis.setHidden(cycleKeys, false);
    rowVis.setHidden([...rowKeys, HIDE_ADD_ONS_ID], false);
  };

  /*
   * Remembered per page and per matrix title (filter memory, owner 2026-10-16),
   * so every company's — or product's — matrix opens in the reader's last order.
   * A remembered year this matrix does not show falls back to the default.
   */
  const [storedSort, setStoredSort] = useRememberedState<SortState>(
    `revenue-matrix-sort-${title}`,
    DEFAULT_SORT,
    parseSortState,
  );
  const sort: SortState =
    storedSort.column.startsWith('cycle:') &&
    !view.cycles.some((cycle) => cycleColumnKey(cycle.id) === storedSort.column)
      ? DEFAULT_SORT
      : storedSort;
  const [hover, setHover] = useState<CrossHair>(NO_CROSSHAIR);
  /**
   * `col: null` pins the row alone (a label click). The pin remembers which
   * years were showing, because a column INDEX means another year once one is
   * hidden or shown.
   */
  const [pin, setPin] = useState<{ rowKey: string; col: number | null; shape: string } | null>(null);
  const [active, setActive] = useState<{ r: number; c: number }>({ r: 0, c: -1 });
  const gridRef = useRef<HTMLDivElement>(null);

  const cycleCount = view.cycles.length;
  /** Grid column of the Total figure; cycles are 0 … cycleCount − 1, the label is −1. */
  const totalCol = cycleCount;
  const shape = view.cycles.map((cycle) => cycle.id).join('|');

  const rows = useMemo(() => {
    const sorted = sortRows(view.rows, toMatrixSort(sort, view));
    if (sort.column !== 'share') return sorted;
    // A null share (grand total zero or below) sorts last in either direction.
    return [
      ...sorted.filter((row) => shareOf(row) !== null),
      ...sorted.filter((row) => shareOf(row) === null),
    ];
  }, [view, sort]);

  // A pin on a row that has left the data — or made under other years — would
  // highlight the wrong thing; drop it.
  const pinned =
    pin !== null && pin.shape === shape && rows.some((row) => row.key === pin.rowKey) ? pin : null;

  // Clamp the roving stop when the grid shrinks under it.
  const activeR = Math.min(active.r, Math.max(0, rows.length - 1));
  const activeC = Math.min(Math.max(active.c, -1), totalCol);

  /** The layers a cell's `Wash` draws: hover fill and pin lines are independent. */
  const washOf = (rowKey: string | null, col: number | null): { hover: boolean; axis: PinAxis } => {
    const inPinRow = pinned !== null && rowKey !== null && pinned.rowKey === rowKey;
    const inPinCol = pinned !== null && col !== null && pinned.col === col;
    const axis: PinAxis = inPinRow && inPinCol ? 'both' : inPinRow ? 'row' : inPinCol ? 'col' : 'none';
    const hovered =
      (rowKey !== null && hover.rowKey === rowKey) || (col !== null && hover.col === col);
    return { hover: hovered, axis };
  };

  const isAnchor = (rowKey: string, col: number) =>
    pinned !== null && pinned.rowKey === rowKey && pinned.col === col;

  const togglePin = (rowKey: string, col: number | null) => {
    setPin((current) =>
      current !== null && current.rowKey === rowKey && current.col === col && current.shape === shape
        ? null
        : { rowKey, col, shape },
    );
  };

  const onHeaderClick = (column: ColumnId) => {
    setStoredSort(
      sort.column === column
        ? { column, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: column === 'label' ? 'asc' : 'desc' },
    );
  };

  const hoverCell = (rowKey: string | null, col: number | null) => (event: PointerEvent) => {
    if (event.pointerType === 'touch') return;
    setHover({ rowKey, col });
  };

  /* -- roving tabindex ------------------------------------------------- */

  const cellId = (r: number, c: number) => `${r}:${c}`;

  const focusCell = (r: number, c: number) => {
    const target = gridRef.current?.querySelector<HTMLElement>(`[data-cell="${cellId(r, c)}"]`);
    target?.focus();
  };

  const onGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Esc clears a pin — only from inside the grid, and only when there is one,
    // so it never swallows an Esc meant for a dialog or menu elsewhere.
    if (event.key === 'Escape') {
      if (pinned !== null) {
        event.preventDefault();
        setPin(null);
      }
      return;
    }
    const position = positionOf(event.target);
    if (position === null) return;
    const { r, c } = position;
    const lastR = rows.length - 1;
    let next: { r: number; c: number } | null = null;

    switch (event.key) {
      case 'ArrowUp':
        next = { r: Math.max(0, r - 1), c };
        break;
      case 'ArrowDown':
        next = { r: Math.min(lastR, r + 1), c };
        break;
      case 'ArrowLeft':
        next = { r, c: Math.max(-1, c - 1) };
        break;
      case 'ArrowRight':
        next = { r, c: Math.min(totalCol, c + 1) };
        break;
      case 'Home':
        next = event.ctrlKey ? { r: 0, c: -1 } : { r, c: -1 };
        break;
      case 'End':
        next = event.ctrlKey ? { r: lastR, c: totalCol } : { r, c: totalCol };
        break;
      default:
        return;
    }
    event.preventDefault();
    setActive(next);
    focusCell(next.r, next.c);
  };

  const onGridFocus = (event: FocusEvent<HTMLDivElement>) => {
    const position = positionOf(event.target);
    if (position === null) return;
    const { r, c } = position;
    // A custom label's link is not the roving stop; only real cells move it.
    if ((event.target as HTMLElement).dataset.cell !== undefined) setActive({ r, c });
    const row = rows[r];
    if (row) setHover({ rowKey: row.key, col: c >= 0 ? c : null });
  };

  /** The pointer left the grid: fall back to the keyboard-focus crosshair, if focus is inside. */
  const onGridPointerLeave = () => {
    const focused = document.activeElement;
    const position =
      focused !== null && gridRef.current?.contains(focused) ? positionOf(focused) : null;
    const row = position === null ? undefined : rows[position.r];
    if (position === null || row === undefined) {
      setHover(NO_CROSSHAIR);
      return;
    }
    setHover({ rowKey: row.key, col: position.c >= 0 ? position.c : null });
  };

  const onGridBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && gridRef.current?.contains(next)) return;
    setHover(NO_CROSSHAIR);
  };

  const tabIndexOf = (r: number, c: number) => (r === activeR && c === activeC ? 0 : -1);

  /* -- export ---------------------------------------------------------- */

  const onExport = () => {
    // The visible rows × years, with their recomputed totals — what is on screen.
    const csv = matrixToCsv({ ...view, rows }, currencyCode, subjectName, rowHeader);
    saveBlob(csvFileName, new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  };

  /* -- headers --------------------------------------------------------- */

  const sortStateOf = (column: ColumnId): 'asc' | 'desc' | null =>
    sort.column === column ? sort.dir : null;

  /** Only the sorted header carries `aria-sort`; the rest omit it. */
  const ariaSortOf = (column: ColumnId): 'ascending' | 'descending' | undefined => {
    const state = sortStateOf(column);
    return state === null ? undefined : state === 'asc' ? 'ascending' : 'descending';
  };

  const sortButton = (column: ColumnId, label: ReactNode, align: 'left' | 'right') => (
    <button
      type="button"
      onClick={() => onHeaderClick(column)}
      className={cx(
        'group/sort inline-flex items-center gap-1 rounded-sm font-medium transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none',
        align === 'right' && 'flex-row-reverse',
        sortStateOf(column) !== null && 'text-fg',
      )}
    >
      <span>{label}</span>
      <SortChevron state={sortStateOf(column)} />
    </button>
  );

  const rowCount = rows.length;
  /** Long table: scroll inside the panel with sticky edges (see the scroll-box note). */
  const scrolls = rowCount > SCROLL_THRESHOLD;

  const wrapTable = (table: ReactElement): ReactElement =>
    scrolls ? <div className={SCROLL_BOX}>{table}</div> : <TableWrap>{table}</TableWrap>;

  /* -- the frozen row label -------------------------------------------- */

  const xeroBadge = (row: VisibleMatrixRow) =>
    row.isUncatalogued ? (
      <Badge tone="neutral" variant="outline" className="shrink-0">
        Not in catalogue
      </Badge>
    ) : null;

  const rowLabelCell = (row: VisibleMatrixRow, r: number): ReactNode => {
    const rowPinned = pinned !== null && pinned.rowKey === row.key && pinned.col === null;

    if (renderRowLabel === undefined) {
      // The label IS the pin button.
      return (
        <button
          type="button"
          data-cell={cellId(r, -1)}
          tabIndex={tabIndexOf(r, -1)}
          aria-label={`Pin ${rowName(row)} row`}
          aria-pressed={rowPinned}
          onClick={() => togglePin(row.key, null)}
          className="relative flex w-full min-w-0 items-baseline gap-2 rounded-sm text-left"
        >
          <span className="min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{row.label}</span>
          <Sublabel text={row.sublabel} />
          {xeroBadge(row)}
        </button>
      );
    }

    // A custom label (a link): pin through a full-cell overlay button underneath it.
    return (
      <>
        <button
          type="button"
          data-cell={cellId(r, -1)}
          tabIndex={tabIndexOf(r, -1)}
          aria-label={`Pin ${rowName(row)} row`}
          aria-pressed={rowPinned}
          onClick={() => togglePin(row.key, null)}
          className="absolute inset-0 h-full w-full rounded-none focus-visible:-outline-offset-2"
        />
        <span className="pointer-events-none relative flex w-full min-w-0 items-baseline gap-2">
          <span
            data-row-label={r}
            className="pointer-events-auto min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]"
          >
            {renderRowLabel(row)}
          </span>
          <Sublabel text={row.sublabel} />
          {xeroBadge(row)}
        </span>
      </>
    );
  };

  return (
    <Panel className="min-w-0">
      <PanelHeader
        title={`${subjectName} by ${rowHeader.toLowerCase()}`}
        description={`${basisLabel} · ${currencyCode} · ${rowCount.toLocaleString()} ${rowNoun(rowHeader, rowCount)} · ${cycleCount.toLocaleString()} ${
          cycleCount === 1 ? 'cycle' : 'cycles'
        }`}
        {...(matrix.rows.length > 0
          ? {
              actions: (
                /*
                 * Wraps below `sm`, where the header stacks and this row takes
                 * the full width — three controls do not fit 320px on one line.
                 */
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <ColumnManager
                    layout={cyclePicker}
                    labelOf={(key) => cycleNames.get(key) ?? key}
                    title="Columns"
                    itemNoun="year"
                    reorderable={false}
                    freezable={false}
                  />
                  <ColumnManager
                    layout={rowPicker}
                    labelOf={(key) => rowNames.get(key) ?? key}
                    lockedKeys={switchHeldKeys}
                    lockReasonOf={() => `Hidden by ${HIDE_ADD_ONS_LABEL}`}
                    title={rowsTitle}
                    itemNoun={rowItemNoun}
                    reorderable={false}
                    freezable={false}
                    {...(addOnSwitch ? { headerSlot: addOnSwitch } : {})}
                  />
                  {rowCount > 0 && cycleCount > 0 && (
                    <Button size="sm" variant="secondary" leadingIcon={<DownloadIcon />} onClick={onExport}>
                      Export CSV
                    </Button>
                  )}
                </div>
              ),
            }
          : {})}
      />

      {(cycleVis.saveFailed || rowVis.saveFailed) && (
        <div className="px-4 pt-3">
          <Notice tone="danger">
            What you hid here could not be saved to your account. It still applies until you leave this page.
          </Notice>
        </div>
      )}

      {matrix.rows.length === 0 ? (
        <EmptyState title="No revenue to show" />
      ) : rowCount === 0 || cycleCount === 0 ? (
        <EmptyState
          title={cycleCount === 0 ? 'Every year is hidden' : `Every ${rowItemNoun} is hidden`}
          action={
            <Button size="sm" variant="secondary" onClick={showEverything}>
              Show all
            </Button>
          }
        />
      ) : (
        <div
          ref={gridRef}
          onKeyDown={onGridKeyDown}
          onFocus={onGridFocus}
          onBlur={onGridBlur}
          onPointerLeave={onGridPointerLeave}
        >
          {wrapTable(
            <Table>
              <caption className="sr-only">
                {title}: {subjectName}
              </caption>
              <THead>
                <TR>
                  <TH
                    frozen="left"
                    frozenOffset={0}
                    aria-sort={ariaSortOf('label')}
                    // Frozen is already sticky (z-20); scrolling only adds the top edge.
                    className={cx('min-w-40 sm:min-w-52', scrolls && 'top-0')}
                    onPointerEnter={hoverCell(null, null)}
                  >
                    {sortButton('label', rowHeader, 'left')}
                    {scrolls && <HeadRule />}
                  </TH>

                  {view.cycles.map((cycle, index) => (
                    <TH
                      key={cycle.id}
                      numeric
                      aria-sort={ariaSortOf(cycleColumnKey(cycle.id))}
                      className={cx('min-w-24', scrolls ? STICKY_HEAD_CELL : 'relative')}
                      onPointerEnter={hoverCell(null, index)}
                    >
                      <Wash {...washOf(null, index)} />
                      <span className="relative">{sortButton(cycleColumnKey(cycle.id), cycle.name, 'right')}</span>
                      {scrolls && <HeadRule />}
                    </TH>
                  ))}

                  <TH
                    numeric
                    aria-sort={ariaSortOf('total')}
                    className={cx('min-w-24 border-l border-border', scrolls ? STICKY_HEAD_CELL : 'relative')}
                    onPointerEnter={hoverCell(null, totalCol)}
                  >
                    <Wash {...washOf(null, totalCol)} />
                    <span className="relative">{sortButton('total', 'Total', 'right')}</span>
                    {scrolls && <HeadRule />}
                    {scrolls && <LeftRule />}
                  </TH>

                  <TH
                    numeric
                    aria-sort={ariaSortOf('share')}
                    className={cx('min-w-20', scrolls && STICKY_HEAD_CELL)}
                    onPointerEnter={hoverCell(null, null)}
                  >
                    {sortButton('share', 'Share', 'right')}
                    {scrolls && <HeadRule />}
                  </TH>
                </TR>
              </THead>

              <TBody>
                {rows.map((row, r) => {
                  const name = rowName(row);
                  const share = shareOf(row);

                  return (
                    <TR key={row.key}>
                      {/* The frozen row label: pins the row (see the header comment). */}
                      <TH
                        scope="row"
                        frozen="left"
                        frozenOffset={0}
                        // Frozen = `position: sticky`, already the containing block for the overlays.
                        className="max-w-56 min-w-40 whitespace-normal sm:max-w-72 sm:min-w-52"
                        onPointerEnter={hoverCell(row.key, null)}
                      >
                        <Wash {...washOf(row.key, null)} />
                        {rowLabelCell(row, r)}
                      </TH>

                      {row.cells.map((cell, c) => {
                        const cycle = view.cycles[c];
                        const exact = cell === null ? null : formatMoneyText(cell, currencyCode);
                        const cellName = `${name}, ${cycle?.name ?? ''}: ${exact ?? 'no revenue'}`;
                        const button = (
                          <button
                            type="button"
                            data-cell={cellId(r, c)}
                            tabIndex={tabIndexOf(r, c)}
                            aria-label={cellName}
                            onClick={() => togglePin(row.key, c)}
                            className="absolute inset-0 h-full w-full rounded-none focus-visible:-outline-offset-2"
                          />
                        );

                        return (
                          <TD
                            key={cycle?.id ?? c}
                            numeric
                            className={cx('relative whitespace-nowrap', HEAT_CLASS[heatLevel(cell, view.maxCell)])}
                            onPointerEnter={hoverCell(row.key, c)}
                          >
                            <Wash {...washOf(row.key, c)} anchor={isAnchor(row.key, c)} />
                            <span aria-hidden="true" className="relative">
                              {cell === null ? (
                                <NoFigure />
                              ) : (
                                <Figure amount={cell} currencyCode={currencyCode} />
                              )}
                            </span>
                            {exact === null ? (
                              button
                            ) : (
                              <Tooltip label={exact} describes={false} className="absolute inset-0">
                                {button}
                              </Tooltip>
                            )}
                          </TD>
                        );
                      })}

                      <TD
                        numeric
                        className="relative whitespace-nowrap border-l border-border"
                        onPointerEnter={hoverCell(row.key, totalCol)}
                      >
                        <Wash {...washOf(row.key, totalCol)} anchor={isAnchor(row.key, totalCol)} />
                        <span aria-hidden="true" className="relative">
                          {/* null: every visible cell of this kept row is empty — a dash, never 0. */}
                          {row.total === null ? (
                            <NoFigure />
                          ) : (
                            <Figure amount={row.total} currencyCode={currencyCode} strong />
                          )}
                        </span>
                        {(() => {
                          const exactTotal =
                            row.total === null ? null : formatMoneyText(row.total, currencyCode);
                          const totalButton = (
                            <button
                              type="button"
                              data-cell={cellId(r, totalCol)}
                              tabIndex={tabIndexOf(r, totalCol)}
                              aria-label={`${name}, total: ${exactTotal ?? 'no revenue'}`}
                              onClick={() => togglePin(row.key, totalCol)}
                              className="absolute inset-0 h-full w-full rounded-none focus-visible:-outline-offset-2"
                            />
                          );
                          return exactTotal === null ? (
                            totalButton
                          ) : (
                            <Tooltip label={exactTotal} describes={false} className="absolute inset-0">
                              {totalButton}
                            </Tooltip>
                          );
                        })()}
                      </TD>

                      <TD
                        numeric
                        className="relative text-xs text-fg-secondary tabular"
                        onPointerEnter={hoverCell(row.key, null)}
                      >
                        <Wash {...washOf(row.key, null)} />
                        <span className="relative">
                          {share === null ? <NoFigure /> : percentText(share)}
                        </span>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>

              <TBody>
                <TR className="border-t border-hairline-strong">
                  <TH
                    scope="row"
                    frozen="left"
                    frozenOffset={0}
                    // Frozen is already sticky (z-10); scrolling only adds the bottom edge.
                    className={cx('align-top', scrolls && 'bottom-0')}
                    // Right edge + the row's top rule, in both modes (see TOTALS_LABEL_SHADOW).
                    style={TOTALS_LABEL_SHADOW}
                    onPointerEnter={hoverCell(null, null)}
                  >
                    <span className="text-sm font-semibold text-fg">Total</span>
                  </TH>

                  {view.columnTotals.map((total, c) => (
                    <TD
                      key={view.cycles[c]?.id ?? c}
                      numeric
                      verticalAlign="top"
                      className={cx('whitespace-nowrap', scrolls ? STICKY_FOOT_CELL : 'relative')}
                      onPointerEnter={hoverCell(null, c)}
                    >
                      <Wash {...washOf(null, c)} />
                      <span className="relative flex flex-col items-end gap-0.5">
                        {total === null ? (
                          <NoFigure />
                        ) : (
                          <ExactFigure amount={total} currencyCode={currencyCode} />
                        )}
                        <YoyChange value={view.yoy[c] ?? null} />
                      </span>
                      {scrolls && <FootRule />}
                    </TD>
                  ))}

                  <TD
                    numeric
                    verticalAlign="top"
                    className={cx('whitespace-nowrap border-l border-border', scrolls ? STICKY_FOOT_CELL : 'relative')}
                    onPointerEnter={hoverCell(null, totalCol)}
                  >
                    <Wash {...washOf(null, totalCol)} />
                    <span className="relative">
                      {view.grandTotal === null ? (
                        <NoFigure />
                      ) : (
                        <ExactFigure amount={view.grandTotal} currencyCode={currencyCode} />
                      )}
                    </span>
                    {scrolls && <FootRule />}
                    {scrolls && <LeftRule />}
                  </TD>

                  <TD
                    numeric
                    verticalAlign="top"
                    className={cx('text-xs text-fg-secondary tabular', scrolls && STICKY_FOOT_CELL)}
                    onPointerEnter={hoverCell(null, null)}
                  >
                    {isNonPositive(view.grandTotal) ? <NoFigure /> : percentText('100.0')}
                    {scrolls && <FootRule />}
                  </TD>
                </TR>
              </TBody>
            </Table>,
          )}
        </div>
      )}
    </Panel>
  );
}
